import api from './client';

export interface HealthTest {
  id: string;
  testName: string;
  testeeName: string;
  testDate: string;
  result: string;
  comment?: string;
  description?: string;
  cost: number;
  notes?: string;
  birdId: string;
  // Enhanced Health Test Fields
  testType?: 'DIAGNOSTIC' | 'PREVENTIVE' | 'VACCINATION' | 'SCREENING' | 'FOLLOW_UP' | 'EMERGENCY';
  urgency?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  testResultEnum?: 'NORMAL' | 'ABNORMAL' | 'POSITIVE' | 'NEGATIVE' | 'PENDING' | 'INCONCLUSIVE' | 'CRITICAL';
  veterinarianName?: string;
  clinicName?: string;
  followUpRequired?: boolean;
  followUpDate?: string;
  referenceRanges?: any;
  testValues?: any;
  recommendations?: string;
  medicationPrescribed?: string[];
  nextTestDue?: string;
  documents?: string[];
  relatedDiseaseIds?: number[];
  bird?: {
    id: string;
    bandId: string;
    speciesId?: string;
  };
  relatedDiseases?: Array<{
    id: number;
    name: string;
    description?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHealthTestDto {
  testName: string;
  testeeName: string;
  testDate: string;
  result: string;
  comment?: string;
  description?: string;
  cost: number;
  notes?: string;
  birdId: string;
  // Enhanced fields
  testType?: string;
  urgency?: string;
  testResultEnum?: string;
  veterinarianName?: string;
  clinicName?: string;
  followUpRequired?: boolean;
  followUpDate?: string;
  referenceRanges?: any;
  testValues?: any;
  recommendations?: string;
  medicationPrescribed?: string[];
  nextTestDue?: string;
  documents?: string[];
  relatedDiseaseIds?: number[];
}

export interface UpdateHealthTestDto extends Partial<CreateHealthTestDto> {}

export interface HealthStatistics {
  totalBirds: number;
  totalHealthTests: number;
  recentTests: number;
  pendingTests: number;
  overdueCheckups: number;
  healthStatusDistribution: Array<{
    status: string;
    count: number;
  }>;
  testTypeDistribution: {
    [key: string]: number;
  };
  urgencyDistribution: {
    [key: string]: number;
  };
}

export interface HealthAlert {
  id: string;
  birdId: string;
  birdBandId: string;
  type: 'OVERDUE_CHECKUP' | 'CRITICAL_RESULT' | 'FOLLOW_UP_DUE' | 'VACCINATION_DUE';
  alertType: 'OVERDUE_CHECKUP' | 'CRITICAL_RESULT' | 'FOLLOW_UP_DUE' | 'VACCINATION_DUE';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  message: string;
  dueDate?: string;
  createdAt: string;
}

export interface BirdHealthSummary {
  birdId: string;
  bandId: string;
  healthStatus: string;
  vaccinationStatus: string;
  lastHealthCheck?: string;
  nextHealthCheck?: string;
  weight?: number;
  healthNotes?: string;
  currentMedications: string[];
  allergies: string[];
  chronicConditions: string[];
  recentTests: HealthTest[];
  upcomingTests: HealthTest[];
  healthAlerts: HealthAlert[];
}

export const healthTestsApi = {
  // Existing CRUD operations
  getAll: async (): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>('health-tests');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching health tests:', error);
      return [];
    }
  },

  getById: async (id: string): Promise<HealthTest> => {
    const response = await api.get<HealthTest>(`health-tests/${id}`);
    return response.data;
  },

  getByBirdId: async (birdId: string): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>(`health-tests/bird/${birdId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching health tests by bird:', error);
      return [];
    }
  },

  create: async (data: CreateHealthTestDto): Promise<HealthTest> => {
    const response = await api.post<HealthTest>('health-tests', data);
    return response.data;
  },

  update: async (id: string, data: UpdateHealthTestDto): Promise<HealthTest> => {
    const response = await api.put<HealthTest>(`health-tests/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`health-tests/${id}`);
  },

  // Enhanced health monitoring endpoints
  getStatistics: async (): Promise<HealthStatistics> => {
    try {
      const response = await api.get<HealthStatistics>('health-monitoring/statistics');
      return response.data;
    } catch (error) {
      console.error('Error fetching health statistics:', error);
      // Return default statistics structure
      return {
        totalBirds: 0,
        totalHealthTests: 0,
        recentTests: 0,
        pendingTests: 0,
        overdueCheckups: 0,
        healthStatusDistribution: [],
        testTypeDistribution: {},
        urgencyDistribution: {},
      };
    }
  },

  getAlerts: async (): Promise<HealthAlert[]> => {
    try {
      const response = await api.get<HealthAlert[]>('health-monitoring/alerts');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching health alerts:', error);
      return [];
    }
  },

  getBirdHealthSummary: async (birdId: string): Promise<BirdHealthSummary> => {
    const response = await api.get<BirdHealthSummary>(`health-monitoring/bird/${birdId}/summary`);
    return response.data;
  },

  updateBirdHealthStatus: async (birdId: string, healthStatus: string): Promise<void> => {
    await api.post(`health-monitoring/bird/${birdId}/update-status`, { healthStatus });
  },

  scheduleHealthCheckup: async (birdId: string, checkupDate: string): Promise<void> => {
    await api.post(`health-monitoring/bird/${birdId}/schedule-checkup`, { checkupDate });
  },

  runHealthMonitoring: async (): Promise<void> => {
    await api.post('health-monitoring/run-monitoring');
  },

  // Filter and search operations
  getByTestType: async (testType: string): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>(`health-tests/filter/type/${testType}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching health tests by type:', error);
      return [];
    }
  },

  getByUrgency: async (urgency: string): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>(`health-tests/filter/urgency/${urgency}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching health tests by urgency:', error);
      return [];
    }
  },

  getPendingFollowUps: async (): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>('health-tests/pending-follow-ups');
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching pending follow-ups:', error);
      return [];
    }
  },

  getByDiseaseId: async (diseaseId: number): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>(`health-tests/disease/${diseaseId}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching health tests by disease:', error);
      return [];
    }
  },

  // Search and Filter functions
  searchHealthTests: async (query: string): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>(`health-tests/search?q=${encodeURIComponent(query)}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error searching health tests:', error);
      return [];
    }
  },

  getHealthTestsByDateRange: async (startDate: string, endDate: string): Promise<HealthTest[]> => {
    try {
      const response = await api.get<HealthTest[]>(`health-tests/date-range?start=${startDate}&end=${endDate}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching health tests by date range:', error);
      return [];
    }
  },
}; 